/**
 * SceneGraphUtils - HTML scene graph representation for debugging.
 */

import type { World } from '../game/World.js';

export interface SceneGraphDebugNode {
  title: string;
  data?: Record<string, any>;
  children?: (SceneGraphDebugNode|null)[];
}

export interface ISceneGraphDebugNodeGenerator {
  generateDebugNode(): SceneGraphDebugNode | null;
}

export class SceneGraphUtils {
  public static dumpHtml(world: World): string {
    return this.generateTreeHtmlPage(world.generateDebugNode()!);
  }

  private static generateTreeHtmlPage(tree: SceneGraphDebugNode): string {
    /* ---------- build markup ---------- */
    function renderNode(node: SceneGraphDebugNode): string {
      const children =
        node.children?.filter(
          (c): c is SceneGraphDebugNode => c !== null
        ) ?? [];

      /* stringify metadata as JSON --------------------------------------- */
      const meta = node.data
        ? JSON.stringify(node.data, null, 2)
          .replace(/</g, '&lt;')          // minimal escaping
          .replace(/>/g, '&gt;')
        : '';

      return `
        <li tabindex="0">
          <div class="node-title">
            ${node.title}
            ${node.children && node.children.length > 0 ? ' (' + node.children.length + ') &#11166;' : ''}
          </div>
          ${meta ? `<pre class="node-meta">${meta}</pre>` : ''}
          ${
  children.length
    ? `<ul class="hidden">${children.map(renderNode).join('')}</ul>`
    : ''
}
        </li>`;
    }

    const treeHtml = `<ul class="tree-root">${renderNode(tree)}</ul>`;

    /* ---------- full HTML page ---------- */
    return `<!DOCTYPE html>
  <html lang="en">
  <head>
  <meta charset="UTF-8">
  <title>Scene Graph</title>
  <style>
    body{font-family:Arial,sans-serif;padding:20px}
    ul{list-style:none;padding-left:20px;position:relative}
    ul::before{content:'';border-left:1px solid #ccc;position:absolute;top:0;bottom:0;left:0}
    li{position:relative;margin:.5em 0;padding-left:1em;outline:none}
    li::before{content:'';border-top:1px solid #ccc;position:absolute;top:1em;left:0;width:1em}
    .node-title{font-weight:bold;cursor:pointer}
    /* meta shown as JSON -------------------------------------------------- */
    .node-meta{margin-left:10px;font-size:.85em;color:#555;white-space:pre-wrap}
    .hidden{display:none}
    .selected{background:#e0f0ff;border-radius:4px}
    .controls{margin-bottom:10px}
    button{margin-right:8px}
  </style>
  </head>
  <body>
  <h1>Scene Graph</h1>
  <div class="controls">
    <button id="expand-all">Expand All</button>
    <button id="collapse-all">Collapse All</button>
  </div>
  ${treeHtml}
  
  <script>
  /* ---------- utilities ---------- */
  const $$ = (sel, root=document)=>Array.from(root.querySelectorAll(sel));
  const isVisible = el => !!(el.offsetParent);
  
  /* toggle a single node */
  function toggleNode(li, expand){
    const ul = li.querySelector(':scope > ul');
    if(!ul) return;
    if(expand===undefined) ul.classList.toggle('hidden');
    else ul.classList.toggle('hidden', !expand);
  }
  
  /* ---------- click to expand / collapse ---------- */
  document.addEventListener('click', e=>{
    const title = e.target.closest('.node-title');
    if(!title) return;
    toggleNode(title.parentElement);
  });
  
  /* ---------- expand-all / collapse-all ---------- */
  document.getElementById('expand-all').onclick = ()=>
    $$('ul').forEach(ul=>ul.classList.remove('hidden'));
  
  document.getElementById('collapse-all').onclick = ()=>
    $$('ul:not(.tree-root)').forEach(ul=>ul.classList.add('hidden'));
  
  /* ---------- keyboard navigation ---------- */
  document.addEventListener('keydown', e=>{
    const li = document.activeElement;
    if(!li || li.tagName!=='LI') return;
  
    const visibleLis = $$('.tree-root li').filter(isVisible);
    const idx = visibleLis.indexOf(li);
    if(idx === -1) return;
  
    let target = null;
  
    switch(e.key){
      case 'ArrowDown':
        target = visibleLis[idx+1] || null;
        break;
      case 'ArrowUp':
        target = visibleLis[idx-1] || null;
        break;
      case 'ArrowRight': {
        const child = li.querySelector(':scope > ul');
        if(child && child.classList.contains('hidden')){
          toggleNode(li,true);             // expand
        }else if(child){                   // already open → first child
          const first = child.querySelector(':scope > li');
          if(first) target = first;
        }
        break;
      }
      case 'ArrowLeft': {
        const child = li.querySelector(':scope > ul');
        if(child && !child.classList.contains('hidden')){
          toggleNode(li,false);            // collapse
        }else{                             // already collapsed → parent
          target = li.parentElement.closest('li');
        }
        break;
      }
    }
  
    if(target){
      e.preventDefault();
      target.focus();
    }
  });
  
  /* ---------- focus highlight ---------- */
  document.addEventListener('focusin', e=>{
    $$('.selected').forEach(li=>li.classList.remove('selected'));
    if(e.target.tagName==='LI') e.target.classList.add('selected');
  });
  </script>
  </body>
  </html>`;
  }
}
